import { MkIcon } from "@/mk/MkIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Pencil } from "lucide-react";

import { api } from "@/api/client";
import type { Page } from "@/api/client";
import { useWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import { useAuth } from "@/app/auth";
import { useBranding } from "@/app/branding";
import { DASHBOARD_GROUP, canGroup } from "@/app/navigation";
import { useNavAccess, useWorkspaceContext } from "@/app/Shell";
import type { WorkspaceContext } from "@/app/Shell";
import { MkCompanyModal } from "@/mk/MkCompanyModal";
import { MkProfileModal } from "@/mk/MkProfileModal";
import { PmCompanySwitcher } from "@/pm/PmCompanySwitcher";
import { PmGlobalSearch } from "@/pm/PmGlobalSearch";
import { PmLocationModal } from "@/pm/PmLocationModal";
import { PmProjectCard } from "@/pm/PmProjectCard";
import { PmScroller } from "@/pm/PmScroller";
import type { ProjectRow } from "@/pm/PmProjectCard";
import {
    WEATHER_KEYS, fetchPrayerAndHijri, fetchWeather, hijriLabel, weatherIcon, zonedNow,
} from "@/pm/homeWidgets";
import { useUserLocation } from "@/pm/useUserLocation";

import { HomeAnnouncements } from "./HomeAnnouncements";

const CLOCK_TICK_MS = 30_000;

type Dialog = "" | "profile" | "company" | "switch_company";

interface HomeLink {
    id: string;
    label: string;
    hint: string;
    icon: string;
    color: string;
    sequence: number;
    group?: string;
    appTag?: string;
    visible?(context: WorkspaceContext | undefined): boolean;
    dialog?: Dialog;
    route?: string;
    modes?: Workspace[];
}

const LINKS: HomeLink[] = [
    { id: "promaster_simplified_monthly_report", label: "home.link.monthlyReport",
      hint: "home.link.monthlyReportHint", icon: "fa-line-chart", color: "monthly-report",
      sequence: 4, group: "promaster_reports.group_read_monthly_report", route: "/executive",
      modes: ["execution"] },
    { id: "promaster_performance_center", label: "home.link.dashboard",
      hint: "home.link.dashboardHint", icon: "fa-tachometer", color: "dashboard", sequence: 5,
      route: "/dashboard", modes: ["execution"], group: DASHBOARD_GROUP },
    { id: "promaster_design_dashboard", label: "home.link.designDashboard",
      hint: "home.link.designDashboardHint", icon: "fa-tachometer", color: "dashboard", sequence: 5,
      route: "/design-dashboard", modes: ["design"], group: DASHBOARD_GROUP },
    { id: "promaster_consulting_dashboard", label: "home.link.consultingDashboard",
      hint: "home.link.consultingDashboardHint", icon: "fa-tachometer", color: "dashboard", sequence: 5,
      route: "/design-dashboard", modes: ["consulting"], group: DASHBOARD_GROUP },
    { id: "promaster_ai", label: "nav.ai", hint: "home.link.aiHint",
      icon: "fa-magic", color: "primary", sequence: 6, route: "/ai", appTag: "promaster_ai.ai_app" },
    { id: "promaster_discuss", label: "home.link.discuss", hint: "home.link.discussHint",
      icon: "fa-comments", color: "warning", sequence: 7, route: "/discuss" },
    { id: "profile", label: "home.link.profile", hint: "home.link.profileHint",
      icon: "fa-user-circle", color: "primary", sequence: 10, dialog: "profile" },
    { id: "company", label: "home.link.company", hint: "home.link.companyHint",
      icon: "fa-building", color: "success", sequence: 20, dialog: "company",
      visible: (context) => Boolean(context?.is_org_admin && context?.is_owner_org) },
    { id: "switch_company", label: "home.link.switchCompany", hint: "home.link.switchCompanyHint",
      icon: "fa-exchange", color: "danger", sequence: 25, dialog: "switch_company",
      visible: (context) => Boolean(context?.can_switch_company) },
];

const STATES: Record<string, { label: string; color: string }> = {
    draft: { label: "home.state.draft", color: "warning" },
    stakeholder_approval: { label: "home.state.stakeholderApproval", color: "info" },
    stop: { label: "home.state.stopped", color: "danger" },
    project_ready: { label: "home.state.running", color: "success" },
    cancel: { label: "home.state.cancelled", color: "danger" },
    close: { label: "home.state.closed", color: "secondary" },
    exported: { label: "card.state.exported", color: "info" },
};

const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"] as const;

const pad = (value: number) => String(value).padStart(2, "0");

function greetingKey(timezone?: string): string {
    const hour = Math.floor(zonedNow(timezone).minutes / 60);
    if (hour < 12) {
        return "home.morning";
    }
    return hour < 17 ? "home.afternoon" : "home.evening";
}

function todayLabel(locale: string | undefined, timezone?: string): string {
    const options: Intl.DateTimeFormatOptions = {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
        calendar: "gregory",
    };
    if (timezone) {
        options.timeZone = timezone;
    }
    try {
        return new Date().toLocaleDateString(locale, options);
    } catch {
        delete options.timeZone;
        return new Date().toLocaleDateString(locale, options);
    }
}

function prayerDisplay(t: (key: string) => string, timings: Record<string, string> | undefined,
                       timezone?: string) {
    if (!timings) {
        return null;
    }
    const nowMin = zonedNow(timezone).minutes;
    const entries: { name: string; h: number; m: number; totalMin: number }[] = [];
    for (const name of PRAYERS) {
        const value = timings[name];
        if (!value) {
            continue;
        }
        const [h = 0, m = 0] = value.split(":").map(Number);
        entries.push({ name: t(`home.prayer.${name.toLowerCase()}`), h, m, totalMin: h * 60 + m });
    }
    const first = entries[0];
    const last = entries[entries.length - 1];
    if (!first || !last) {
        return null;
    }
    const nextIdx = entries.findIndex((entry) => entry.totalMin > nowMin);
    const next = nextIdx === -1
        ? { ...first, totalMin: first.totalMin + 1440 }
        : entries[nextIdx] ?? first;
    const prev = nextIdx === 0
        ? { ...last, totalMin: last.totalMin - 1440 }
        : entries[nextIdx === -1 ? entries.length - 1 : nextIdx - 1] ?? last;
    const minsSincePrev = nowMin - prev.totalMin;
    const minsToNext = next.totalMin - nowMin;
    const isSince = minsSincePrev >= 0 && minsSincePrev <= 30;
    const target = isSince ? prev : next;
    const diff = isSince ? minsSincePrev : minsToNext;
    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;
    const timeText = hrs
        ? `${hrs}${t("home.hourUnit")} ${mins}${t("home.minuteUnit")}`
        : `${mins}${t("home.minuteUnit")}`;
    return {
        name: target.name,
        time: `${pad(target.h)}:${pad(target.m)}`,
        countdown: `${isSince ? t("home.since") : t("home.in")} ${timeText}`,
    };
}

export function HomeScreen() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const access = useNavAccess();
    const context = useWorkspaceContext();
    const [search, setSearch] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [dialog, setDialog] = useState<Dialog>("");
    const [locationOpen, setLocationOpen] = useState(false);
    const [, setTick] = useState(0);
    const searchInput = useRef<HTMLInputElement>(null);
    const { location, loading: locationLoading } = useUserLocation();

    useEffect(() => {
        const timer = window.setInterval(() => setTick((value) => value + 1), CLOCK_TICK_MS);
        return () => window.clearInterval(timer);
    }, []);

    const workspace = useWorkspace();
    const projects = useQuery<Page<ProjectRow>>({
        queryKey: ["home", "projects", workspace],
        queryFn: () => api.list<ProjectRow>("projects", { per_page: 200, sort: "name" }),
    });

    const widgets = useQuery({
        queryKey: [
            "home", "widgets",
            location?.latitude ?? 0, location?.longitude ?? 0, location?.timezone ?? "",
        ],
        staleTime: 30 * 60 * 1000,
        retry: false,
        enabled: Boolean(location?.city && (location.latitude || location.longitude)),
        queryFn: async () => {
            if (!location) {
                throw new Error("No location");
            }
            const [weather, prayer] = await Promise.allSettled([
                fetchWeather(location), fetchPrayerAndHijri(location),
            ]);
            return {
                weather: weather.status === "fulfilled" ? weather.value : null,
                prayer: prayer.status === "fulfilled" ? prayer.value : null,
            };
        },
    });

    const links = useMemo(() => {
        if (!access.data || !context.data) {
            return [];
        }
        return [...LINKS]
            .sort((a, b) => a.sequence - b.sequence)
            .filter((link) => (!link.visible || link.visible(context.data))
                              && (!link.modes || link.modes.includes(workspace))
                              && (!link.appTag || access.data.data.apps[link.appTag] !== false)
                              && canGroup(access.data.data, link.group));
    }, [access.data, context.data, workspace]);

    const rows = projects.data?.data ?? [];
    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return rows;
        }
        return rows.filter((row) => [row.name, row.code].some(
            (value) => (value || "").toLowerCase().includes(term)));
    }, [rows, search]);

    const locale = (user?.lang || "").replace("_", "-") || undefined;
    const timezone = location?.timezone || widgets.data?.prayer?.timezone
        || widgets.data?.weather?.timezone || "";
    const today = todayLabel(locale, timezone);

    const weather = widgets.data?.weather;
    const weatherKey = weather ? WEATHER_KEYS[weather.code] : undefined;
    const weatherLabel = weatherKey ? t(weatherKey) : "";
    const prayer = prayerDisplay(t, widgets.data?.prayer?.timings, timezone);
    const hijri = hijriLabel(
        widgets.data?.prayer?.hijri,
        (user?.lang || "").startsWith("ar") ? "ar" : "en",
        locale,
    );

    function onLinkClick(link: HomeLink) {
        if (link.dialog) {
            setDialog(link.dialog);
        } else if (link.route) {
            navigate(link.route);
        }
    }

    function toggleSearch() {
        const isMobile = window.matchMedia("(max-width: 640px)").matches;
        if (!isMobile) {
            setSearchOpen(true);
            searchInput.current?.focus();
            return;
        }
        setSearchOpen((open) => {
            if (open) {
                setSearch("");
            } else {
                window.setTimeout(() => searchInput.current?.focus(), 60);
            }
            return !open;
        });
    }

    return (
        <section className="pm-home">
            <header className="pm-home-hero">
                <div className="pm-home-hero-main">
                    {branding?.logo_url && (
                        <img src={branding.logo_url} alt="" className="pm-home-hero-logo" />
                    )}
                    <div className="pm-home-hero-text">
                        <h1 className="pm-home-hero-greeting">
                            {t(greetingKey(timezone))}{user?.name && <>, <span>{user.name}</span></>}
                        </h1>
                        <div className="pm-home-hero-date">
                            <span>{today}</span>
                            {hijri && (
                                <>
                                    <span className="pm-home-hero-date-sep" aria-hidden="true">·</span>
                                    <span className="pm-home-hero-date-hijri">{hijri}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="pm-home-hero-side">
                    <div className="pm-home-chips">
                        {location?.city ? (
                            <button type="button" className="pm-home-chip pm-home-chip-action"
                                    onClick={() => setLocationOpen(true)}
                                    title={t("home.location.change")}>
                                {weather
                                    ? <MkIcon name={weatherIcon(weather.code)} />
                                    : <MapPin size={14} aria-hidden="true" />}
                                <span className="pm-home-chip-copy">
                                    <span>{location.city}{weather ? ` · ${weather.temp}°` : ""}</span>
                                    {weather && <><span className="pm-home-chip-sep"> · </span><span className="pm-home-chip-detail">{weatherLabel}</span></>}
                                </span>
                                <Pencil size={12} className="pm-home-chip-edit"
                                        aria-hidden="true" />
                            </button>
                        ) : (
                            <button type="button" className="pm-home-chip pm-home-chip-action"
                                    onClick={() => setLocationOpen(true)}
                                    title={t("home.location.change")}>
                                <MapPin size={14} aria-hidden="true" />
                                <span>
                                    {locationLoading
                                        ? t("home.location.loading")
                                        : t("home.location.set")}
                                </span>
                            </button>
                        )}
                        {prayer && (
                            <div className="pm-home-chip pm-home-chip-prayer">
                                <MkIcon name="fa-clock-o" />
                                <span className="pm-home-chip-copy"><span>{prayer.name} · {prayer.time}</span><span className="pm-home-chip-sep"> · </span><span className="pm-home-chip-detail">{prayer.countdown}</span></span>
                            </div>
                        )}
                    </div>
                    <PmGlobalSearch />
                </div>
            </header>

            <HomeAnnouncements />

            {links.length > 0 && (
                <div className="pm-home-section">
                    <div className="pm-home-section-head">
                        <h3 className="pm-home-section-title">
                            <MkIcon name="fa-bolt" />
                            <span>{t("home.quickLinks")}</span>
                        </h3>
                    </div>
                    <PmScroller rowClassName="pm-home-links">
                        {links.map((link) => (
                            <div key={link.id} className={`pm-home-link is-${link.color}`}
                                 onClick={() => onLinkClick(link)}>
                                <span className="pm-home-link-icon"><MkIcon name={link.icon} /></span>
                                <span className="pm-home-link-body">
                                    <span className="pm-home-link-name">{t(link.label)}</span>
                                    {link.hint && <span className="pm-home-link-hint">{t(link.hint)}</span>}
                                </span>
                            </div>
                        ))}
                    </PmScroller>
                </div>
            )}

            <div className="pm-home-section">
                <div className="pm-home-section-head">
                    <h3 className="pm-home-section-title">
                        <MkIcon name="fa-bullseye" />
                        <span>{t("home.projectsOverview")}</span>
                    </h3>
                    <div className={`pm-home-search ${searchOpen ? "is-open" : ""}`}>
                        <button type="button" className="pm-home-search-toggle"
                                aria-label={t("home.searchProjects")}
                                title={t("home.searchProjects")}
                                onClick={toggleSearch}>
                            <MkIcon name="fa-search" />
                        </button>
                        <input type="text" className="pm-home-search-input"
                               placeholder={t("home.searchProjects")} ref={searchInput}
                               value={search} onChange={(event) => setSearch(event.target.value)} />
                        {search && (
                            <button type="button" className="pm-home-search-clear"
                                    aria-label={t("home.clear")}
                                    onClick={() => {
                                        setSearch("");
                                        searchInput.current?.focus();
                                    }}>
                                <MkIcon name="fa-times" />
                            </button>
                        )}
                    </div>
                </div>

                {!projects.isLoading && !filtered.length && (
                    <div className="pm-home-empty">
                        <MkIcon name="fa-bullseye" />
                        <span>{t("home.noProjects")}</span>
                    </div>
                )}

                {filtered.length > 0 && (
                    <PmScroller rowClassName="pm-home-projects">
                        {filtered.map((row) => (
                            <div className="pm-home-project" key={row.id}>
                                <PmProjectCard row={row} states={STATES}
                                               onOpen={() => navigate(`/projects?type=${workspace}&open=${row.id}`)} />
                            </div>
                        ))}
                    </PmScroller>
                )}
            </div>

            <PmLocationModal open={locationOpen} current={location}
                             onClose={() => setLocationOpen(false)} />
            {dialog === "profile" && <MkProfileModal open onClose={() => setDialog("")} />}
            {dialog === "company" && <MkCompanyModal open onClose={() => setDialog("")} />}
            {dialog === "switch_company" && <PmCompanySwitcher onClose={() => setDialog("")} />}
        </section>
    );
}
